import { Delete, Error as ErrorIcon } from '@mui/icons-material';
import { Chip, Grid } from '@mui/material';
import { CustomFields as CustomFieldsValueObject, Location, Report } from '@nx-smartmonkey/shared/domain';
import { dateFormatShortDate, pastDateTime } from '@nx-smartmonkey/shared/helpers';
import {
  HSpacer,
  RoutalLoader,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  RoutalTooltip,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import CrossIcon from '../../../../../assets/svg/CrossIcon';
import InstructionIcon from '../../../../../assets/svg/InstructionIcon';
import Constraints from '../../../../../assets/svg/right-panel-tab-icons/ConstraintsIcon';
import InformationIcon from '../../../../../assets/svg/right-panel-tab-icons/InformationIcon';
import StopReportHistoricalIcon from '../../../../../assets/svg/right-panel-tab-icons/StopReportHistoricalIcon';
import StopReportIcon from '../../../../../assets/svg/right-panel-tab-icons/StopReportIcon';
import StopTasks from '../../../../../assets/svg/right-panel-tab-icons/StopTasks';
import Survey from '../../../../../assets/svg/right-panel-tab-icons/Survey';
import { CustomFields } from '../../../../../components/custom-fields/CustomFields';
import { RatingStars } from '../../../../../components/RatingStars';
import {
  checkR,
  renderArray,
  // renderCheckbox,
  renderInputLocation,
  RenderInputPhone,
  renderInputText,
  renderLinkedClient,
  renderPickup,
  renderTimewindows,
} from '../../../../../components/right-panel-info/Common';
import { InfoContainer } from '../../../../../components/right-panel-info/RightPanel.styled';
import { STOP_STATUS } from '../../../../../constants/Stops';
import { Driver } from '../../../../../domain/driver/Driver';
import { Event } from '../../../../../domain/event/Event';
import { Project } from '../../../../../domain/project/Project';
import { Stop } from '../../../../../domain/stop/Stop';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import { useRetrieveActivePlan } from '../../../../../hooks/plan/useRetrieveActivePlan';
import { useRetrieveActivePlanGeoFences } from '../../../../../hooks/plan/useRetrieveActivePlanGeoFences';
import { useRetrieveRightPanelInfo } from '../../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useCreateStopPickup } from '../../../../../hooks/stop/useCreateStopPickup';
import useCreateStopTask from '../../../../../hooks/stop/useCreateStopTask';
import { useDeleteStopPickup } from '../../../../../hooks/stop/useDeleteStopPickup';
import useDeleteStopTask from '../../../../../hooks/stop/useDeleteStopTask';
import { useUpdateStop } from '../../../../../hooks/stop/useUpdateStop';
import useUpdateStopTask from '../../../../../hooks/stop/useUpdateStopTask';
import { useRetrieveAllSupervisors } from '../../../../../hooks/supervisor/useRetrieveAllSupervisors';
import { useRetrieveUserCredentials } from '../../../../../hooks/user/useRetrieveUserCredentials';
import { Accordion } from '../../../../../react-components/Accordion';
import Button from '../../../../../react-components/Button';
import Text from '../../../../../react-components/Text';
import { CreatePickupData } from '../../../../../repositories/stop/StopRepository';
import { getTaskColorByStatus } from '../../../plan-details/StatusBar';
import { BrokenConstraintsTooltip, hasStopBrokenConstraints } from '../../BrokenConstraintsTooltip';
import { getStopSurveyRatingTagInfo } from '../../StopRatingTag';
import StopReport, { getReportStatusColor } from './StopReport';

interface StopInfoSectionProps {
  stop: Stop;
  planDrivers: Driver[];
  section?: number;
  project: Project;
  handleUpdate: (...args: any[]) => any;
}

const StopInfoSection = ({ stop, planDrivers, section, project, handleUpdate }: StopInfoSectionProps) => {
  const { run: createStopTask, isLoading: loadingCreateStopTaks } = useCreateStopTask({ stop });
  const { run: onUpdateTask } = useUpdateStopTask({ stop });
  const { run: onDeleteTask, isLoading: loadingDeletingStopTask } = useDeleteStopTask({ stop });
  const { run: createPickup } = useCreateStopPickup({ stop });
  const { run: deletePickup } = useDeleteStopPickup({ stop });
  const [currentReport, setCurrentReport] = useState<Report | undefined>(undefined);

  useEffect(() => {
    if ((stop.reports ?? []).length > 0) {
      const planReport = stop.getCurrentReport();
      setCurrentReport(planReport ?? stop.reports![0]);
    } else {
      setCurrentReport(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stop.reports]);

  if (!project.view) {
    return null;
  }

  const processEvent = (event: Event): string => {
    switch (event.type) {
      case `smartmonkey.planner.2.planner.stop.rescheduled`: {
        if (!event.created_by) {
          return `event.optimized`;
        }
        if (event.meta?.driver_id) {
          return `event.driver_changed`;
        }
        if (event.meta?.order) {
          return `event.reordered`;
        }
        return `event.rescheduled`;
      }
      case `smartmonkey.planner.2.planner.stop.reported`: {
        if (event.meta.status === `completed`) {
          return `event.completed`;
        }
        if (event.meta.status === `canceled`) {
          return `event.canceled`;
        }
        break;
      }
      case `smartmonkey.planner.2.planner.stop.moved`: {
        return `event.moved`;
      }
      case `smartmonkey.planner.2.planner.stop.created`: {
        return `event.created`;
      }
      case `smartmonkey.planner.2.planner.stop.updated`: {
        return `event.updated`;
      }
      case `smartmonkey.planner.2.planner.stop.deleted`: {
        return `event.deleted`;
      }
    }

    return `unknown event`;
  };

  if (section === undefined) {
    return (
      <Grid container justifyContent="center" style={{ height: `100%`, overflow: `hidden` }} alignItems="center">
        <RoutalLoader width={60} />
      </Grid>
    );
  }

  switch (section) {
    case 0:
      return (
        <div style={{ height: `100%`, overflowY: `auto`, minHeight: `300px` }}>
          <InfoContainer>
            <Text
              variant="h6"
              style={{ color: RoutalPalette.primary40 }}
              isBold
              intlId="clients.details.group.contact"
            />

            <VSpacer medium />

            {checkR(project.view?.client.reference_person) ? (
              <>
                {renderInputText({
                  labelIntlId: `properties.stop.reference_person`,
                  variant: `outlined`,
                  property: `reference_person`,
                  handleUpdate,
                  data: stop,
                })}
                <VSpacer medium />
              </>
            ) : null}

            {checkR(project.view?.client.email) ? (
              <>
                {renderInputText({
                  labelIntlId: `properties.stop.email`,
                  variant: `outlined`,
                  property: `email`,
                  type: `email`,
                  handleUpdate,
                  data: stop,
                })}
                <VSpacer medium />
              </>
            ) : null}

            {checkR(project?.view?.client.phone) && (
              <>
                <RenderInputPhone
                  data={stop}
                  defaultCountry={
                    project?.location?.country_code ? project?.location?.country_code.toUpperCase() : undefined
                  }
                  property="phone"
                  handleUpdate={handleUpdate}
                  labelIntlId="properties.stop.phone"
                  placeholderIntlId="properties.stop.phone"
                />
                <VSpacer medium />
              </>
            )}

            {checkR(project.view?.client.url) ? (
              <>
                {renderInputText({
                  labelIntlId: `properties.stop.url`,
                  variant: `outlined`,
                  property: `url`,
                  type: `url`,
                  handleUpdate,
                  data: stop,
                })}
                <VSpacer medium />
              </>
            ) : null}

            {checkR(project.view?.client.comments) &&
              renderInputText({
                labelIntlId: `properties.stop.comments`,
                variant: `outlined`,
                property: `comments`,
                multiline: true,
                rowsMin: 4,
                handleUpdate,
                data: stop,
              })}
          </InfoContainer>

          {(project.custom_fields?.client ?? []).length > 0 ? (
            <InfoContainer>
              <Text variant="h6" style={{ color: RoutalPalette.primary40 }} isBold intlId="custom_fields.title" />
              <VSpacer medium />
              <CustomFields
                onUpdate={(customFields: CustomFieldsValueObject) => {
                  handleUpdate(`custom_fields`, customFields);
                }}
                customFields={stop.custom_fields}
                projectCustomFields={project.custom_fields?.client ?? []}
              />
            </InfoContainer>
          ) : null}

          {stop.customer_text_field && project.customers.fields.find((field) => field.id === `customer_text_field`) ? (
            <InfoContainer style={{ paddingTop: 0 }}>
              <Grid container direction="row" alignItems="center">
                <Grid item>
                  <InstructionIcon
                    alt=""
                    style={{ height: `15px`, width: `15px`, paddingRight: `5px`, color: RoutalPalette.warning.medium }}
                  />
                </Grid>
                <HSpacer small />
                <Grid item>
                  <Text
                    variant="h6"
                    style={{ color: RoutalPalette.primary40 }}
                    isBold
                    intlId="properties.stop.customer_text_field"
                  />
                </Grid>
              </Grid>

              <VSpacer medium />

              <Text variant="label" style={{ wordBreak: `break-word` }}>
                {stop.customer_text_field}
              </Text>
            </InfoContainer>
          ) : null}

          <InfoContainer>
            <Text
              variant="h6"
              style={{ color: RoutalPalette.primary40 }}
              isBold
              intlId="clients.details.group.events"
            />

            <VSpacer medium />

            <Grid container direction="column">
              {stop.events?.length === 0 && <Text variant="label" intlId="clients.details.group.events.no_events" />}
              {stop.events
                ?.sort((o1, o2) => (o1.created_at < o2.created_at ? 1 : -1))
                .map((event: any, idx: number) => {
                  return (
                    <Grid
                      container
                      direction="row"
                      style={{
                        padding: `4px 0`,
                      }}
                    >
                      <Grid item style={{ width: `48px` }}>
                        <Text variant="label">{pastDateTime(event.created_at)}</Text>
                      </Grid>
                      <Grid item>
                        <Text intlId={processEvent(event)} />
                      </Grid>
                    </Grid>
                  );
                })}
            </Grid>
          </InfoContainer>

          <VSpacer large />
        </div>
      );
    case 1:
      return (
        <div style={{ height: `100%`, overflowY: `auto`, minHeight: `300px` }}>
          <InfoContainer>
            <Text
              variant="h6"
              isBold
              intlId="clients.details.group.constraints"
              style={{ color: RoutalPalette.primary40 }}
            />

            <VSpacer medium />

            {checkR(project.view?.constraints.time_windows) ? (
              <>
                {renderTimewindows(`properties.stop`, `time_windows`, handleUpdate, stop)}
                <VSpacer medium />
              </>
            ) : null}

            {stop.type === `pickup` && checkR(project.view?.constraints.max_delivery_time) && (
              <>
                {renderInputText({
                  labelIntlId: `properties.stop.max_delivery_time`,
                  variant: `outlined`,
                  property: `max_delivery_time`,
                  type: `number`,
                  handleUpdate: (property, value) => handleUpdate(property, value * 60),
                  data: stop,
                  suffix: `min`,
                  computed: (value) => (value ? value / 60 : null),
                })}
                <VSpacer medium />
              </>
            )}

            {checkR(project.view?.constraints.reward) && (
              <>
                {renderInputText({
                  labelIntlId: `properties.stop.reward`,
                  variant: `outlined`,
                  property: `reward`,
                  type: `number`,
                  handleUpdate,
                  data: stop,
                })}
                <VSpacer medium />
              </>
            )}

            {renderInputText({
              labelIntlId: `properties.stop.duration`,
              variant: `outlined`,
              property: `duration`,
              type: `number`,
              handleUpdate: (property, value) => handleUpdate(property, value ? value * 60 : null),
              data: stop,
              suffix: `min`,
              computed: (value) => (value ? value / 60 : undefined),
            })}

            <VSpacer medium />

            {stop.type !== `pickup` && checkR(project.view?.constraints.weight) ? (
              <>
                {renderInputText({
                  labelIntlId: `properties.stop.weight`,
                  variant: `outlined`,
                  property: `weight`,
                  type: `number`,
                  handleUpdate,
                  data: stop,
                  suffix: project?.units?.weight,
                })}
                <VSpacer medium />
              </>
            ) : null}

            {stop.type !== `pickup` && checkR(project.view?.constraints.volume) ? (
              <>
                {renderInputText({
                  labelIntlId: `properties.stop.volume`,
                  variant: `outlined`,
                  property: `volume`,
                  type: `number`,
                  handleUpdate,
                  data: stop,
                  suffix: project?.units?.volume,
                })}
                <VSpacer medium />
              </>
            ) : null}

            {checkR(project.view?.constraints.provide_requires) ? (
              <>
                {renderArray(`properties.stop`, `requires`, handleUpdate, stop)}
                <VSpacer medium />
              </>
            ) : null}

            {checkR(project.view?.constraints.cluster) ? (
              <>
                {renderInputText({
                  labelIntlId: `properties.stop.cluster`,
                  property: `cluster`,
                  variant: `outlined`,
                  handleUpdate,
                  data: stop,
                })}
                <VSpacer medium />
              </>
            ) : null}

            {stop.type !== `pickup` &&
              checkR(project.view?.constraints.pickups) &&
              renderPickup({
                prefix: `properties.stop`,
                handleCreatePickup: ({ pickupData }: { pickupData: CreatePickupData }) => {
                  createPickup({ pickupData });
                },
                handleDeletePickup: () => {
                  deletePickup();
                },
                pickup: stop.pickup,
              })}
          </InfoContainer>
          <VSpacer large />
        </div>
      );
    case 2:
      return (
        <Grid
          container
          direction="column"
          wrap="nowrap"
          style={{ height: `100%`, width: `100%`, overflowY: `auto`, minHeight: `300px` }}
        >
          <Grid
            container
            direction="column"
            wrap="nowrap"
            style={{ height: `calc(100% - 60px)`, width: `100%`, overflowY: `auto` }}
          >
            {(stop.tasks ?? []).length > 0 ? (
              stop.tasks?.map((task, index) => {
                return (
                  <Accordion
                    id={task.id}
                    expandIconStyles={{
                      color: `${RoutalPalette.secondary40}`,
                      position: `absolute`,
                      top: 18,
                      right: 20,
                    }}
                    headerStyles={{
                      position: `relative`,
                      minHeight: `30px`,
                      padding: `8px 0`,
                      height: `60px`,
                      borderBottom: `1px solid ${RoutalPalette.neutral10}`,
                    }}
                    headerContent={
                      <Grid
                        container
                        alignItems="center"
                        style={{ height: `100%`, paddingLeft: `20px`, width: `calc(100% - 40px)` }}
                        wrap="nowrap"
                      >
                        <Grid item container alignItems="center" style={{ width: `unset` }}>
                          <div
                            style={{
                              width: `22px`,
                              height: `22px`,
                              borderRadius: `50%`,
                              backgroundColor: getTaskColorByStatus(task.status),
                            }}
                          />
                        </Grid>

                        <HSpacer small />

                        <Grid item container alignItems="center">
                          {task.label ? (
                            <Text variant="body-large">{task.label}</Text>
                          ) : (
                            <Text variant="body-large" intlId="no_name" style={{ color: `#CDCFD0` }} />
                          )}
                        </Grid>
                      </Grid>
                    }
                    contentStyle={{ flex: 1 }}
                  >
                    <Grid
                      container
                      direction="column"
                      wrap="nowrap"
                      style={{
                        backgroundColor: `#EEEEEE`,
                        padding: `20px`,
                        borderBottom: `1px solid ${RoutalPalette.neutral10}`,
                      }}
                    >
                      {renderInputText({
                        labelIntlId: `properties.stop.tasks.label`,
                        variant: `outlined`,
                        property: `label`,
                        handleUpdate: (property, val) => onUpdateTask({ task, label: val }),
                        data: task,
                      })}
                      <VSpacer medium />

                      {checkR(project.view?.task.barcode) ? (
                        <>
                          {renderInputText({
                            labelIntlId: `properties.stop.tasks.barcode`,
                            variant: `outlined`,
                            property: `barcode`,
                            handleUpdate: (property, val) => onUpdateTask({ task, barcode: val }),
                            data: task,
                          })}
                          <VSpacer medium />
                        </>
                      ) : null}

                      {checkR(project.view?.task.comments) ? (
                        <>
                          {renderInputText({
                            labelIntlId: `properties.stop.tasks.comments`,
                            variant: `outlined`,
                            property: `comments`,
                            handleUpdate: (property, val) => onUpdateTask({ task, comments: val }),
                            multiline: true,
                            rowsMin: 4,
                            data: task,
                          })}
                          <VSpacer medium />
                        </>
                      ) : null}

                      {(project.custom_fields?.task ?? []).length > 0 ? (
                        <CustomFields
                          ignoreEnabled
                          onUpdate={(customFields: CustomFieldsValueObject) => {
                            onUpdateTask({ task, custom_fields: customFields });
                          }}
                          customFields={task.custom_fields}
                          projectCustomFields={project.custom_fields?.task ?? []}
                        />
                      ) : null}

                      <Grid direction="row" alignItems="center" justifyContent="flex-end" style={{ display: `flex` }}>
                        <Button
                          variant="text"
                          onClick={() => {
                            if (!loadingDeletingStopTask) {
                              onDeleteTask({ task });
                            }
                          }}
                          style={{
                            border: `none`,
                            margin: 0,
                            padding: 0,
                            userSelect: `none`,
                          }}
                        >
                          <Delete style={{ width: `20px`, height: `20px`, color: `${RoutalPalette.grey7}` }} />
                          <Text
                            intlId="properties.stop.tasks.delete"
                            style={{ cursor: `pointer`, fontSize: `12px`, color: `${RoutalPalette.grey7}` }}
                          />
                        </Button>
                      </Grid>
                    </Grid>
                  </Accordion>
                );
              })
            ) : (
              <Grid container alignItems="center" justifyContent="center" style={{ height: `80px` }}>
                <Text intlId="properties.stop.tasks.empty" style={{ fontSize: `14px` }} />
              </Grid>
            )}
          </Grid>

          {stop.status === STOP_STATUS.pending ? (
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              style={{
                backgroundColor: `${RoutalPalette.neutral00}`,
                height: `60px`,
                boxShadow: `0px 0px 6px #00000029`,
              }}
            >
              <Button
                style={{ width: `100%`, margin: `0px 20px` }}
                styleType="secondary"
                disabled={loadingCreateStopTaks}
                intlId={loadingCreateStopTaks ? `loading` : `properties.stop.tasks.create`}
                onClick={() => createStopTask()}
              />
            </Grid>
          ) : null}
        </Grid>
      );
    case 3:
      return (
        <Grid
          container
          direction="column"
          wrap="nowrap"
          style={{ height: `100%`, width: `100%`, overflowY: `auto`, minHeight: `300px` }}
        >
          <Grid container direction="column" wrap="nowrap" style={{ height: `100%`, width: `100%`, overflowY: `auto` }}>
            {currentReport ? (
              <StopReport project={project} currentReport={currentReport} stop={stop} planDrivers={planDrivers} />
            ) : (
              <>
                {(stop.reports ?? []).length === 0 ? (
                  <Grid container alignItems="center" justifyContent="center" style={{ height: `80px` }}>
                    <Text intlId="properties.stop.reports.empty" style={{ fontSize: `14px` }} />
                  </Grid>
                ) : null}
              </>
            )}
          </Grid>

          {currentReport ? (
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              style={{
                backgroundColor: `${RoutalPalette.neutral00}`,
                height: `60px`,
                boxShadow: `0px 0px 6px #00000029`,
              }}
            >
              {stop
                .reports!.sort((a, b) => (a.created_at >= b.created_at ? -1 : 1))
                .map((report: Report, idx: number) => (
                  <>
                    {idx > 0 ? <HSpacer small /> : null}
                    <RoutalTooltip
                      title={dateFormatShortDate(report.created_at)}
                      overlayInnerStyle={{
                        color: `${RoutalPalette.neutral00}`,
                      }}
                      backgroundColor={getReportStatusColor(report)}
                    >
                      <>
                        {idx === 0 ? (
                          <StopReportIcon
                            isSelected={Boolean(currentReport.id === report.id)}
                            style={{
                              cursor: `pointer`,
                              height: `32px`,
                              width: `32px`,
                              color: getReportStatusColor(report),
                            }}
                            onClick={() => setCurrentReport(report)}
                          />
                        ) : (
                          <StopReportHistoricalIcon
                            active={Boolean(currentReport.id === report.id)}
                            style={{
                              cursor: `pointer`,
                              height: `33px`,
                              width: `32px`,
                              color: getReportStatusColor(report),
                            }}
                            onClick={() => setCurrentReport(report)}
                          />
                        )}
                      </>
                    </RoutalTooltip>
                  </>
                ))}
            </Grid>
          ) : null}
        </Grid>
      );
    case 4:
      return (
        <div style={{ height: `100%`, overflowY: `auto`, minHeight: `300px` }}>
          {stop.survey ? (
            <InfoContainer>
              <Text variant="h6" style={{ color: RoutalPalette.primary40 }} isBold intlId="properties.stop.survey" />

              <VSpacer medium />

              <Text variant="label" intlId="properties.stop.survey.rating" />

              <VSpacer medium />

              <RatingStars rating={stop.survey!.rating ?? 0} />

              <VSpacer medium />

              <Text variant="label" intlId="properties.stop.survey.rating_tag" />

              <VSpacer medium />

              {getStopSurveyRatingTagInfo(stop.survey)}

              <VSpacer medium />

              <Text variant="label" intlId="properties.stop.survey.comments" />

              <VSpacer medium />

              <Text style={{ wordBreak: `break-word` }}>{stop.survey.comments}</Text>
            </InfoContainer>
          ) : (
            <Grid container justifyContent="center" alignItems="center" style={{ height: `80px` }}>
              <Text intlId="properties.stop.no_survey" style={{ fontSize: `14px` }} />
            </Grid>
          )}
        </div>
      );
    default:
      return null;
  }
};

interface StopBodyProps {
  stop: Stop;
  planDrivers: Driver[];
  activeProject: Project;
}

export const StopBody = ({ stop, planDrivers, activeProject }: StopBodyProps) => {
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { run: updateStop } = useUpdateStop();
  const { data: activePlan } = useRetrieveActivePlan();
  const { data: planGeoFences } = useRetrieveActivePlanGeoFences();
  const { data: supervisors } = useRetrieveAllSupervisors();

  const { detailTab, setRightPanelInfo } = useRetrieveRightPanelInfo();

  const [infoSection, setInfoSection] = useState<number | undefined>(undefined);

  useEffect(() => {
    switch (detailTab) {
      case `constraints`:
        setInfoSection(1);
        break;
      case `tasks`:
        if (userCredentials?.subscription.test(`planner.tasks`) && activeProject.tasks_enabled) {
          setInfoSection(2);
        } else {
          setInfoSection(0);
        }
        break;
      case `reports`:
        setInfoSection(3);
        break;
      case `survey`:
        setInfoSection(4);
        break;
      case `info`:
      default:
        setInfoSection(0);
        break;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTab]);

  const handleUpdate = (label: string, val: any) => {
    updateStop({ stop, changes: { [label]: val } });
  };

  return (
    <Grid container direction="column" wrap="nowrap" style={{ height: `100%` }}>
      <InfoContainer>
        {renderInputText({
          placeholderIntlId: `properties.stop.label`,
          variant: `clean`,
          property: `label`,
          size: `title`,
          handleUpdate,
          data: stop,
        })}
        <VSpacer medium />

        {activeProject?.drivers.hasPinEnabled() && stop.pin && (
          <>
            <Text intlId="properties.stop.pin" variant="label" style={{ marginBottom: `8px` }} />

            <Text style={{ marginLeft: `8px` }}>{stop.pin}</Text>

            <VSpacer medium />
          </>
        )}

        {checkR(activeProject?.view?.client.external_id) && (
          <>
            <Text intlId="properties.stop.external_id" variant="label" style={{ marginBottom: `8px` }} />
            {renderInputText({
              placeholderIntlId: `properties.stop.none`,
              variant: `outlined`,
              property: `external_id`,
              handleUpdate,
              data: stop,
            })}
            <VSpacer medium />
          </>
        )}

        {checkR(activeProject?.view?.client.external_id) && (
          <>
            <Text intlId="properties.stop.client_external_id" variant="label" style={{ marginBottom: `8px` }} />
            {renderInputText({
              placeholderIntlId: `properties.stop.none`,
              variant: `outlined`,
              property: `client_external_id`,
              handleUpdate,
              data: stop,
            })}
            <VSpacer medium />
          </>
        )}

        {renderInputLocation({
          prefix: `properties.stop`,
          property: `location`,
          handleUpdate: (property: string, location?: Location | null) => {
            handleUpdate(property, location);
          },
          data: stop,
        })}

        <VSpacer medium />

        {checkR(activeProject?.view?.client.location_details) && (
          <>
            {renderInputText({
              placeholderIntlId: `properties.stop.location_details`,
              variant: `outlined`,
              property: `location_details`,
              handleUpdate,
              data: stop,
            })}
            <VSpacer medium />
          </>
        )}

        {renderLinkedClient({
          prefix: `properties.stop`,
          property: `client_id`,
          handleUpdate,
          stop,
        })}

        <VSpacer medium />

        {userCredentials?.subscription.test(`supervisors`) && supervisors && supervisors.length > 0 && (
          <Grid container direction="column" wrap="nowrap" style={{ width: `100%` }}>
            <Grid item style={{ width: `100%` }}>
              {/* TODO: CONVERT THIS TO A SUPERVISORS COMPONENT */}
              <RoutalSelect
                id="select-supervisors"
                popoverProps={{
                  options:
                    supervisors.filter((supervisor) => !stop.supervisors.includes(supervisor.id)).length > 0
                      ? supervisors
                          .filter((supervisor) => !stop.supervisors.includes(supervisor.id))
                          .map(
                            (supervisor): RoutalSelectMultiSelectOption => ({
                              key: supervisor.name,
                              closeOnClick: false,
                              onClick: () => {
                                if (stop.supervisors.includes(supervisor.id)) {
                                  handleUpdate(
                                    `supervisors`,
                                    stop.supervisors.filter((id) => id !== supervisor.id)
                                  );
                                } else {
                                  handleUpdate(`supervisors`, [...stop.supervisors, supervisor.id]);
                                }
                              },
                              content: (
                                <Grid
                                  container
                                  direction="row"
                                  wrap="nowrap"
                                  justifyContent="flex-start"
                                  alignItems="center"
                                >
                                  <div
                                    style={{
                                      width: `14px`,
                                      height: `14px`,
                                      backgroundColor: supervisor.color?.value,
                                      borderRadius: `50%`,
                                    }}
                                  />
                                  <HSpacer small />
                                  <Text style={{ cursor: `pointer` }}>{supervisor.name}</Text>
                                </Grid>
                              ),
                            })
                          )
                      : [
                          {
                            key: `no-supervisors`,
                            onClick: () => {},
                            content: (
                              <Grid
                                container
                                direction="row"
                                wrap="nowrap"
                                justifyContent="flex-start"
                                alignItems="center"
                              >
                                <Text intlId="none" />
                              </Grid>
                            ),
                          },
                        ],
                }}
              >
                <Grid item container direction="row" wrap="nowrap" justifyContent="flex-start" alignItems="center">
                  <Text
                    style={{ cursor: `pointer`, color: RoutalPalette.neutral20, paddingLeft: `4px` }}
                    intlId="properties.stop.select_supervisor"
                  />
                </Grid>
              </RoutalSelect>
            </Grid>

            <VSpacer medium />

            <Grid container direction="row">
              {stop.supervisors.map((supervisorId, index) => {
                const supervisor = supervisors.find((supervisor) => supervisor.id === supervisorId);
                if (!supervisor) {
                  return null;
                }

                return (
                  <Grid item>
                    <Chip
                      key={supervisor.id}
                      label={supervisor.name}
                      style={{
                        color: `${RoutalPalette.neutral00}`,
                        backgroundColor: supervisor.color?.value,
                        margin: `2px 8px 2px 0px`,
                      }}
                      onDelete={() => {
                        handleUpdate(
                          `supervisors`,
                          stop.supervisors.filter((id) => id !== supervisor.id)
                        );
                      }}
                      deleteIcon={
                        <CrossIcon
                          style={{
                            fontSize: `10px`,
                            marginLeft: `2px`,
                            marginRight: `8px`,
                            color: `${RoutalPalette.neutral00}`,
                          }}
                        />
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        )}

        {/* {checkR(activeProject?.view?.client.optional) && renderCheckbox(`properties.stop`, `optional`, handleUpdate, stop)} */}
      </InfoContainer>

      <Grid
        container
        direction="row"
        alignItems="center"
        justifyContent="center"
        style={{
          minHeight: `60px`,
          borderTop: `1px solid ${RoutalPalette.neutral10}`,
          borderBottom: `1px solid ${RoutalPalette.neutral10}`,
        }}
      >
        <RoutalTooltip key="stop-information" title={intl.formatMessage({ id: `properties.stop.information` })}>
          <InformationIcon
            isSelected={infoSection === 0}
            style={{
              cursor: infoSection === 0 ? `default` : `pointer`,
            }}
            onClick={() => {
              setRightPanelInfo({ detailTab: `info` });
            }}
          />
        </RoutalTooltip>

        <HSpacer large />

        <RoutalTooltip key="stop-constraints" title={intl.formatMessage({ id: `properties.stop.constraints` })}>
          <Constraints
            isSelected={infoSection === 1}
            style={{
              cursor: infoSection === 1 ? `default` : `pointer`,
            }}
            onClick={() => {
              setRightPanelInfo({ detailTab: `constraints` });
            }}
          />
        </RoutalTooltip>

        {activePlan &&
        hasStopBrokenConstraints({
          stop,
          driver: planDrivers.find((driver: Driver) => driver.id === stop.driver_id),
          planGeoFences: planGeoFences ?? [],
          activeProject,
          userCredentials,
        }) ? (
          <BrokenConstraintsTooltip
            type="stop"
            stop={stop}
            tooltipPlacement="right"
            driver={planDrivers.find((driver: Driver) => driver.id === stop.driver_id)}
          >
            <ErrorIcon
              style={{
                color: RoutalPalette.error.medium,
                fontSize: `16px`,
                backgroundColor: `${RoutalPalette.neutral00}`,
                borderRadius: `50%`,
                marginLeft: `-8px`,
                marginBottom: `20px`,
              }}
            />
          </BrokenConstraintsTooltip>
        ) : null}

        <HSpacer large />

        {userCredentials?.subscription.test(`planner.tasks`) && activeProject.tasks_enabled ? (
          <>
            <RoutalTooltip key="stop-tasks" title={intl.formatMessage({ id: `properties.stop.tasks` })}>
              <StopTasks
                isSelected={infoSection === 2}
                style={{
                  cursor: infoSection === 2 ? `default` : `pointer`,
                }}
                onClick={() => {
                  setRightPanelInfo({ detailTab: `tasks` });
                }}
              />
            </RoutalTooltip>

            <HSpacer large />
          </>
        ) : null}

        <RoutalTooltip key="stop-report" title={intl.formatMessage({ id: `properties.stop.reports` })}>
          <StopReportIcon
            isSelected={infoSection === 3}
            style={{
              cursor: infoSection === 3 ? `default` : `pointer`,
              // color: infoSection === 3 ? RoutalPalette.primary60 : `${RoutalPalette.secondary40}`,
            }}
            onClick={() => {
              setRightPanelInfo({ detailTab: `reports` });
            }}
          />
        </RoutalTooltip>

        {activeProject.isCustomersSurveyEnabled() ? (
          <>
            <HSpacer large />

            <RoutalTooltip key="stop-survey" title={intl.formatMessage({ id: `properties.stop.survey` })}>
              <Survey
                isSelected={infoSection === 4}
                style={{
                  cursor: infoSection === 4 ? `default` : `pointer`,
                }}
                onClick={() => {
                  setRightPanelInfo({ detailTab: `survey` });
                }}
              />
            </RoutalTooltip>
          </>
        ) : null}
      </Grid>

      <StopInfoSection
        stop={stop}
        planDrivers={planDrivers}
        section={infoSection}
        project={activeProject}
        handleUpdate={handleUpdate}
      />
    </Grid>
  );
};
